import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { SubmitButton } from "../../components/shared/buttons/submit-button";
import Input from "../../components/shared/input";
import { Modal } from "../../components/shared/modal/modal";
import ThemedPhoneInput from "../../components/shared/themed-phone-input";
import { useMedicalRecordRequestDetailContext } from "../context/MedicalRecordRequestDetailContext";

export type ContactFormValues = {
  department: string;
  phone: string;
  fax: string;
  email: string;
  is_primary: boolean;
};

const initialFormValues: ContactFormValues = {
  department: "",
  phone: "",
  fax: "",
  email: "",
  is_primary: false,
};

export function EditRequestModal() {
  const {
    isContactModalOpen,
    closeContactModal,
    handleSaveContact,
    isContactSaving,
  } = useMedicalRecordRequestDetailContext();
  const [contactForm, setContactForm] =
    useState<ContactFormValues>(initialFormValues);

  // Reset form when modal opens
  useEffect(() => {
    if (isContactModalOpen) {
      setContactForm(initialFormValues);
    }
  }, [isContactModalOpen]);

  const handleContactFormChange =
    (field: "department" | "email") =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setContactForm((prev: ContactFormValues) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handlePhoneChange = (field: "phone" | "fax") => (value: string) => {
    setContactForm((prev: ContactFormValues) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setContactForm((prev: ContactFormValues) => ({
      ...prev,
      is_primary: event.target.checked,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await handleSaveContact(contactForm);
    } catch (err) {
      console.error("Unable to save contact", err);
    }
  };

  const handleClose = () => {
    setContactForm(initialFormValues);
    closeContactModal();
  };

  return (
    <Modal
      isOpen={isContactModalOpen}
      onClose={handleClose}
      title="Add Contact"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Department"
          value={contactForm.department}
          onChange={handleContactFormChange("department")}
          fullWidth
        />
        <ThemedPhoneInput
          label="Phone"
          value={contactForm.phone}
          onChange={handlePhoneChange("phone")}
          fullWidth
        />
        <ThemedPhoneInput
          label="Fax"
          value={contactForm.fax}
          onChange={handlePhoneChange("fax")}
          fullWidth
        />
        <Input
          label="Email"
          type="email"
          value={contactForm.email}
          onChange={handleContactFormChange("email")}
          fullWidth
        />
        <label className="flex items-center gap-2 text-sm text-terracotta/80">
          <input
            type="checkbox"
            checked={contactForm.is_primary}
            onChange={handleCheckboxChange}
          />
          Primary contact
        </label>
        <div className="flex justify-end gap-2">
          <SubmitButton
            type="button"
            color="beige"
            onClick={handleClose}
            hideArrow
          >
            Cancel
          </SubmitButton>
          <SubmitButton type="submit" isLoading={isContactSaving}>
            Save Contact
          </SubmitButton>
        </div>
      </form>
    </Modal>
  );
}
